import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import {
  changePolygon,
  polygonParameters,
  type PolygonParameters,
} from "../native/shapes";

/** Shape-only fields never overwrite unrelated properties in mixed selections. */
export class NativeShapeInspector {
  private panel: HTMLElement;
  private input(name: string) {
    return document.getElementById(this.prefix + name) as HTMLInputElement;
  }
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private changed: () => void,
    private message: (text: string, error?: boolean) => void,
    private prefix = "native-",
  ) {
    this.panel = document.getElementById(prefix + "shape-properties")!;
    for (const [name, property] of [
      ["sides", "sides"],
      ["star-mode", "star"],
      ["depth", "innerRatio"],
    ] as const) {
      const input = this.input(name);
      input.addEventListener("change", () => {
        if (!pointer.active && (property === "star" || input.value.trim())) {
          try {
            const value =
              property === "star"
                ? input.checked
                : property === "innerRatio"
                  ? (100 - Number(input.value)) / 100
                  : Number(input.value);
            session.replaceMany(
              changePolygon(session.selectedNodes, property, value),
            );
            session.finish();
          } catch (error) {
            message((error as Error).message, true);
          }
        }
        input.blur();
        changed();
      });
      input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          this.render(true);
          input.blur();
          event.stopPropagation();
        }
      });
    }
  }
  render(restoreInput = false) {
    const parameters = this.session.selectedNodes
      .filter((n) => n.kind === "polygon")
      .map(polygonParameters);
    this.panel.hidden = !parameters.length;
    const shared = (key: keyof PolygonParameters) =>
      parameters.every((p) => p[key] === parameters[0]?.[key]);
    const sides = this.input("sides"),
      star = this.input("star-mode"),
      depth = this.input("depth");
    for (const field of [sides, star, depth])
      field.disabled = this.pointer.active || !parameters.length;
    if (restoreInput || document.activeElement !== sides)
      sides.value =
        parameters.length && shared("sides")
          ? String(parameters[0]!.sides)
          : "";
    sides.placeholder = "Разные";
    star.indeterminate = !shared("star");
    star.checked = !!parameters[0]?.star;
    if (restoreInput || document.activeElement !== depth)
      depth.value =
        parameters.length && shared("innerRatio")
          ? String(Math.round((1 - parameters[0]!.innerRatio) * 10000) / 100)
          : "";
    depth.placeholder = "Разные";
    depth.disabled ||= !parameters.some((p) => p.star);
  }
}
